/**
 * Live vs testing submission routing — pure rules.
 *
 * The submission mode is EXPLICIT end to end. It is derived once, from the
 * duplicate-dialog action plus the server-verified allowlist decision, and is
 * never inferred from UI text, query params, or storage.
 *
 * Both modes run the exact same readiness evaluation and render the exact same
 * warnings. Testing relaxes ONLY the stopping behaviour, and never the payment
 * flow: a testing submission still goes through real checkout, so payment is
 * never silently bypassed.
 */

export type SubmissionMode = "live" | "testing";

export type DuplicateAction = "submit_live" | "submit_testing";

/** Readiness states the confirmation screen can observe. */
export type ReadinessView = "evaluating" | "ready" | "incomplete" | "unavailable";

/**
 * Map the live query lifecycle onto exactly one explicit customer state.
 *
 * The rule that matters: only an ACTIVELY pending request may render
 * "evaluating". Anything settled without a usable answer — lookup error,
 * lookup returned no review, readiness error, readiness settled undefined,
 * or a bounded stall timeout — is "unavailable", never an endless spinner.
 */
export function resolveReadinessView(input: {
  reviewPending: boolean;
  reviewError: boolean;
  reviewId: string | null;
  readinessPending: boolean;
  readinessError: boolean;
  readinessStatus: "ready" | "incomplete" | "unavailable" | null | undefined;
  timedOut?: boolean;
}): ReadinessView {
  if (input.reviewError) return "unavailable";
  // Lookup settled with no row — the claim cannot be verified.
  if (!input.reviewPending && !input.reviewId) return "unavailable";
  if (input.readinessError) return "unavailable";
  // A settled answer always wins, even if it landed after the stall window.
  if (input.readinessStatus && !input.reviewPending && !input.readinessPending) {
    if (input.readinessStatus === "ready") return "ready";
    if (input.readinessStatus === "unavailable") return "unavailable";
    return "incomplete";
  }
  if (input.timedOut) return "unavailable";
  if (input.reviewPending || input.readinessPending) return "evaluating";
  // Settled with no usable answer — never an endless spinner.
  return "unavailable";
}

/**
 * Remount-proof stall anchor. The checking deadline is measured from the FIRST
 * time a claim's readiness panel was observed, so a re-render, remount or
 * refetch can never push the spinner past the bounded window.
 */
const readinessDeadlines = new Map<string, number>();

/** Returns milliseconds left in the bounded checking window for this claim. */
export function readinessMsRemaining(fileNumber: string, now = Date.now()): number {
  const started = readinessDeadlines.get(fileNumber) ?? now;
  if (!readinessDeadlines.has(fileNumber)) readinessDeadlines.set(fileNumber, now);
  return Math.max(0, READINESS_STALL_MS - (now - started));
}

/** Retry restarts the bounded window for this claim (no new review is created). */
export function resetReadinessDeadline(fileNumber: string, now = Date.now()): void {
  readinessDeadlines.set(fileNumber, now);
}


/** Bounded stall window before the checking state converts to unavailable. */
export const READINESS_STALL_MS = 20_000;

export const READINESS_UNAVAILABLE_TITLE = "We Couldn't Verify Your Invoice";
export const READINESS_UNAVAILABLE_BODY =
  "We're temporarily unable to check your invoice. Your uploaded file is safe. Please try again.";
export const READINESS_CHECKING_BODY =
  "We're confirming that we have the minimum information needed for an accurate review.";


/**
 * Derive the mode. Testing is only reachable from the testing action AND an
 * authorized account; everything else — including an unauthorized account
 * clicking a forged testing action — resolves to live.
 */
export function resolveSubmissionMode(
  action: DuplicateAction,
  opts: { canBypass: boolean },
): SubmissionMode {
  return action === "submit_testing" && opts.canBypass === true ? "testing" : "live";
}

/** Options handed to the submit handler for a duplicate-dialog action. */
export function submitOptionsForAction(
  action: DuplicateAction,
  opts: { canBypass: boolean },
): { bypassDuplicate: true; mode: SubmissionMode } {
  return { bypassDuplicate: true, mode: resolveSubmissionMode(action, opts) };
}

export interface ConfirmationView {
  /** Readiness has not resolved yet — show nothing conclusive. */
  evaluating: boolean;
  showSuccessHero: boolean;
  /** The readiness gate (warnings + missing requirements) renders. */
  showRequirements: boolean;
  /** Testing-only explicit "continue anyway" action. */
  showTestingContinue: boolean;
  showServiceSelection: boolean;
  showPayment: boolean;
  /** Payment is NEVER skipped — testing exercises the real checkout. */
  paymentBypassed: false;
  /** Banner copy above service selection, or null. */
  disclosure: string | null;
}

export const TESTING_CONTINUE_DISCLOSURE =
  "Testing Mode — Continuing With Unresolved Requirements";
export const READY_DISCLOSURE = "Invoice Information Complete";

/**
 * Single decision for what the confirmation screen may render.
 * Success UI never precedes readiness evaluation.
 */
export function decideConfirmationView(input: {
  mode: SubmissionMode;
  readiness: ReadinessView;
  testingOverride: boolean;
}): ConfirmationView {
  const { mode, readiness } = input;
  const testingOverride = mode === "testing" && input.testingOverride === true;

  if (readiness === "evaluating") {
    return {
      evaluating: true,
      showSuccessHero: false,
      showRequirements: false,
      showTestingContinue: false,
      showServiceSelection: false,
      showPayment: false,
      paymentBypassed: false,
      disclosure: null,
    };
  }

  if (readiness === "ready") {
    return {
      evaluating: false,
      showSuccessHero: true,
      showRequirements: false,
      showTestingContinue: false,
      showServiceSelection: true,
      showPayment: true,
      paymentBypassed: false,
      disclosure: READY_DISCLOSURE,
    };
  }

  // incomplete / unavailable — identical warnings for both modes.
  const proceed = testingOverride && readiness === "incomplete";
  return {
    evaluating: false,
    showSuccessHero: proceed,
    showRequirements: true,
    showTestingContinue: mode === "testing" && readiness === "incomplete" && !proceed,
    showServiceSelection: proceed,
    showPayment: proceed,
    paymentBypassed: false,
    disclosure: proceed ? TESTING_CONTINUE_DISCLOSURE : null,
  };
}

/**
 * Customer-enterable fields the Optional Claim Information card must expand
 * and highlight. Pure derivation of the readiness result — document-only
 * requirements never produce a manual-entry field.
 */
export function customerEnterableFields(
  readiness: { status: string; requirements: Array<{ field?: "vendor" | "zip" | null }> } | null | undefined,
  showRequirements: boolean,
): Array<"vendor" | "zip"> {
  if (!showRequirements || readiness?.status !== "incomplete") return [];
  return readiness.requirements
    .map((r) => r.field)
    .filter((f): f is "vendor" | "zip" => f === "vendor" || f === "zip");
}
